import { Box, Button, Paper, Typography } from "@mui/material";
import { pluralise } from "./deviceGridHelpers";

interface IDeviceBulkActionBarProps {
  selectedCount: number;
  /** Total rows currently matching the active filter, for "Select all matching". */
  matchingCount: number;
  allMatchingSelected: boolean;
  onDeactivate: () => void;
  onRemove: () => void;
  onClear: () => void;
  onSelectAllMatching: () => void;
  busy: boolean;
}

/**
 * Selection toolbar above the device grid.
 *
 * Deactivate is offered first and styled as the non-destructive option because
 * it is reversible: crew on leave look identical to departed crew in a staleness
 * filter, and deactivating is the recoverable way to act on that ambiguity.
 */
export const DeviceBulkActionBar = (props: IDeviceBulkActionBarProps): JSX.Element | null => {
  const {
    selectedCount,
    matchingCount,
    allMatchingSelected,
    onDeactivate,
    onRemove,
    onClear,
    onSelectAllMatching,
    busy,
  } = props;

  if (selectedCount === 0) return null;

  return (
    <Paper
      elevation={4}
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        px: 1.5,
        py: 1,
        mb: 1,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }} data-testid="bulk-selected-count">
        {pluralise(selectedCount, "device")} selected
      </Typography>
      <Button variant="contained" color="warning" size="small" disabled={busy} onClick={onDeactivate}>
        Deactivate
      </Button>
      <Button variant="contained" color="error" size="small" disabled={busy} onClick={onRemove}>
        Remove permanently
      </Button>
      <Button variant="text" size="small" disabled={busy} onClick={onClear}>
        Clear selection
      </Button>
      <Box sx={{ flexGrow: 1 }} />
      {!allMatchingSelected && matchingCount > selectedCount && (
        <Button variant="outlined" size="small" disabled={busy} onClick={onSelectAllMatching}>
          Select all {matchingCount} matching
        </Button>
      )}
    </Paper>
  );
};
